import { LATIN_NUMBERING_SYSTEM } from "../constants/number";
import { resolveIntlLocale } from "./date";
import type {
  IFormatDeviceLocaleNumberProps,
  IFormatNumberProps,
} from "./number.types";
import { toWesternDigits } from "./western-digits";

// NOTE: One formatter per locale tag — there are three app languages, and a
// chart legend formats a figure per item on every render.
const numberFormatters = new Map<string, Intl.NumberFormat>();

const getNumberFormatter = (locale: string): Intl.NumberFormat => {
  const cached = numberFormatters.get(locale);
  if (cached) return cached;

  const formatter = new Intl.NumberFormat(locale, {
    numberingSystem: LATIN_NUMBERING_SYSTEM,
  });
  numberFormatters.set(locale, formatter);
  return formatter;
};

// NOTE: NSAI-4570 — a count shown in the UI. `toLocaleString()` with no locale
// follows the DEVICE, so under an `ar-EG` device the Knowledge grid printed
// `إجمالي الأجزاء ٤` beside three tiles rendered raw as `4`. This follows the APP
// language instead, like every date formatter, and always prints Western
// digits: `1,234` (en), `1 234` (ru), `1,234` (ar).
export const formatNumber = ({ value, language }: IFormatNumberProps): string =>
  toWesternDigits(
    getNumberFormatter(resolveIntlLocale(language)).format(value),
  );

// NOTE: For the shared formatters that have no app language in reach (token
// counts, costs) and so keep the device locale's grouping and currency layout.
// Only the digits are pinned — the numeral system is the part that mixed.
export const formatDeviceLocaleNumber = ({
  value,
  options,
}: IFormatDeviceLocaleNumberProps): string =>
  toWesternDigits(
    new Intl.NumberFormat(undefined, {
      ...options,
      numberingSystem: LATIN_NUMBERING_SYSTEM,
    }).format(value),
  );
